/**
 * 「本月剩余可花」卡（**J 域 J3**）· 首页与后续小程序首页同一口径
 *
 * 依据：规格 **§12** · `DR-010` 决策三 · J 域 PRD **§4.6**（口径与三态）· **§7.3**
 * （`AC-J3.6`–`AC-J3.9`）· 契约 `contract/spendable.ts` · 纯逻辑 `features/spendable.ts`。
 *
 * ## 🔴 本组件**一个数都不算**
 *
 * | 显示的东西 | 来源 |
 * |---|---|
 * | 可花 / 已透支 / 无预算（三态） | 服务端 `result.kind`（内核 `composeSpendable`） |
 * | 预算 / 已花 / 待缴未付 / 必付未付 / 三构成 | 服务端 `breakdown.*`（G 域唯一函数 + F 域同一聚合） |
 * | 「已占用 84.4%」 | 服务端 `occupiedE4` → `percentText()`（只排版） |
 * | 窗口重置说明 | 服务端 `window.start` → 只**格式化**成 `10/1 重置` |
 *
 * ⇒ 本文件里**没有**除法、没有 `reduce`、没有 `new Date()`、没有金额格式化（一律走 `<Amount>`）。
 *
 * ## ⚠️ 两处刻意的呈现取舍（诚实登记）
 *
 * 1. 🔴 **构成明细常显，不做「点开才展开」**（原型 `01-今天_原型.html:387` 画了个
 *    「看怎么算的」按钮）。理由：端侧测试基座是**零交互**的静态渲染
 *    （`test/render.tsx` 的升级触发线未触发）⇒ 折叠会让"展开后有什么"变成**断言不到的死角**
 *    （这正是 ㊳ 批 `Autocomplete` 弹层踩过的坑）。信息本身一条不少，只是把"点一下"
 *    换成了"直接看得见"。
 * 2. ⚠️ **不画空进度条**：`noBudget` 时没有分母 ⇒ `occupancy` 为 `null`，
 *    组件**整块不渲染**（画一条 0% 的条会让人以为"预算被用光了"）。
 */

import { Alert, Box, Button, Card, CardContent, Divider, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { SpendableKind } from '@jiaying/shared';
import { useNavigate } from 'react-router-dom';
import { getSpendable } from '../api/endpoints.js';
import { Loading } from '../ui/Loading.js';
import { Amount } from '../ui/Amount.js';
import {
  SPENDABLE_NO_BUDGET_HINT,
  SPENDABLE_NO_BUDGET_TITLE,
  toSpendableCard,
} from '../features/spendable.js';

export function SpendableCard() {
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['spendable'], queryFn: getSpendable });

  if (q.isPending) return <Loading py={3} />;
  if (q.isError) {
    return <Alert severity="error">「本月剩余可花」读取失败：{String(q.error)}</Alert>;
  }

  const view = q.data;
  const m = toSpendableCard(view);
  const currency = view.baseCurrency;
  /** 🔴 无预算 ⇒ 构成明细、待缴标注、缺汇率提示**一律不渲染**（见下方注释） */
  const hasBudget = m.kind !== SpendableKind.NoBudget;

  return (
    <Card data-testid="spendable-card" data-anchor={m.title}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
          {/* 🔴 唯一名（`AC-J3.1`）—— 取自契约常量，端侧不重写、不出现第二个名字 */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {m.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {m.resetText}
          </Typography>
        </Stack>

        {/* ── 无预算：显示引导，🔴 **不显示任何数字**（`AC-J3.8`）── */}
        {m.kind === SpendableKind.NoBudget ? (
          <Box data-testid="spendable-nobudget" sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {SPENDABLE_NO_BUDGET_TITLE}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              {SPENDABLE_NO_BUDGET_HINT}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              sx={{ mt: 1 }}
              onClick={() => void navigate('/plan')}
              data-testid="spendable-set-budget"
            >
              去设预算
            </Button>
          </Box>
        ) : (
          <>
            {/* ── 主数值：可花（可含 0）或 已透支额（🔴 不截断为 0 · `AC-J3.9`）── */}
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'baseline', mt: 1 }}>
              {m.prefix === null ? null : (
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'error.main', fontWeight: 600 }}
                  data-testid="spendable-prefix"
                >
                  {m.prefix}
                </Typography>
              )}
              <Amount
                cents={m.cents as never}
                currency={currency}
                colored={false}
                variant="h4"
                data-testid="spendable-amount"
                sx={{ fontWeight: 700, color: m.alarming ? 'error.main' : undefined }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary" component="div">
              {m.formulaText}
            </Typography>

            {/* ── 已占用进度条（🔴 无预算时不渲染；数字全来自服务端 `occupiedE4`）── */}
            {m.occupancy === null ? null : (
              <Box sx={{ mt: 1.5 }} data-testid="spendable-occupancy">
                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Typography variant="caption" color="text.secondary">
                    本月预算
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    已占用 {m.occupancy.text}
                  </Typography>
                </Stack>
                <Box sx={{ mt: 0.5, height: 8, borderRadius: 4, bgcolor: 'action.hover' }}>
                  <Box
                    sx={{
                      width: `${String(m.occupancy.percent)}%`,
                      height: '100%',
                      borderRadius: 4,
                      bgcolor: m.alarming ? 'error.main' : 'primary.main',
                    }}
                  />
                </Box>
              </Box>
            )}
          </>
        )}

        {/* ── 构成明细（`AC-J3.6`：预算 / 已花 / 必付未付三项分列 + 必付三构成）──
            🔴 **无预算时整块不渲染**：`noBudget` 下「已花 / 必付未付」由供数口**刻意给 0**
            （没有月度总预算就没有那个窗口的取数）—— 画出来就是「明明花了钱却显示 ¥0.00」
            这种**用沉默制造的假信息**。`AC-J3.8` 要的就是"不显示数字"。
        */}
        {hasBudget ? (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Box data-testid="spendable-composition">
              {m.composition.map((line) => (
                <Stack
                  key={`${line.key}-${line.label}`}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="baseline"
                  spacing={1}
                  sx={{ pl: line.indent ? 2 : 0, py: 0.25 }}
                  data-testid={`spendable-line-${line.key}-${line.label}`}
                >
                  <Typography variant="caption" color={line.indent ? 'text.secondary' : 'text.primary'}>
                    {line.label}
                  </Typography>
                  {line.cents === null ? (
                    <Typography variant="caption" color="text.secondary">
                      —
                    </Typography>
                  ) : (
                    <Amount cents={line.cents as never} currency={currency} variant="caption" colored={false} />
                  )}
                </Stack>
              ))}
            </Box>

            {/* 🔴 「含待缴」标注：仅 `> 0` 显示（`¥0 未付` 是纯噪音 —— 与 G 域 `unpaidAmount` 同一条纪律）── */}
            {m.unpaidPending === null ? null : (
              <Typography
                variant="caption"
                color="text.secondary"
                component="div"
                sx={{ mt: 0.5 }}
                data-testid="spendable-unpaid-pending"
              >
                已花中含待缴{' '}
                <Amount
                  cents={m.unpaidPending as never}
                  currency={currency}
                  variant="caption"
                  colored={false}
                />{' '}
                未付
              </Typography>
            )}

            {/* 🔴 缺汇率 ⇒ 必须标注（架构 §5.4 铁律 5：不静默、不按 1:1 冒充）── */}
            {m.fxNotice === null ? null : (
              <Alert severity="info" sx={{ mt: 1 }} data-testid="spendable-fx-notice">
                {m.fxNotice}
              </Alert>
            )}
          </>
        ) : null}

        {/* ── 下钻（`AC-J3.7`：必付明细跳 F2 日历看待付项）── */}
        <Button
          size="small"
          sx={{ mt: 1 }}
          onClick={() => void navigate('/plan')}
          data-testid="spendable-drill"
        >
          看必付明细
        </Button>
      </CardContent>
    </Card>
  );
}
